import { AnalyticsService } from "@mssi/analytics-ui-lib";

const isAnalyticsEnabled = process.env.REACT_APP_ENABLE_GA === "true";

class AnalyticsManager {
  private static instance: AnalyticsManager;

  private readonly analyticsService = AnalyticsService.getInstance();

  private initialized = false;

  private static getStoredContext(): Record<string, unknown> {
    const globalWindow = (globalThis as { window?: Window }).window;

    if (!globalWindow?.sessionStorage) {
      return {};
    }

    try {
      const raw = globalWindow.sessionStorage.getItem("ga-custom-context");
      return raw ? JSON.parse(raw) : {};
    } catch (error) {
      console.warn("Failed to parse GA custom context:", error);
      return {};
    }
  }

  public static getInstance(): AnalyticsManager {
    if (!AnalyticsManager.instance) {
      AnalyticsManager.instance = new AnalyticsManager();
    }

    return AnalyticsManager.instance;
  }

  public async initialize(measurementId?: string, customContext?: Record<string, unknown>): Promise<void> {
    console.log("🔵 GA Initialize called - Enabled:", isAnalyticsEnabled);

    if (this.initialized) {
      console.log("✅ GA Already initialized");
      return;
    }

    if (!isAnalyticsEnabled) {
      console.log("🔴 GA is DISABLED (REACT_APP_ENABLE_GA != 'true')");
      return;
    }

    const effectiveMeasurementId = measurementId ?? process.env.REACT_APP_GA_TRACKING_ID;

    if (!effectiveMeasurementId) {
      console.warn("⚠️ Missing GA measurement id. Analytics disabled.");
      return;
    }

    try {
      const baseContext = AnalyticsManager.getStoredContext();
      console.log("🔵 Initializing GA with ID:", effectiveMeasurementId);
      await this.analyticsService.init(effectiveMeasurementId, {
        ...baseContext,
        ...customContext,
      });
      this.initialized = true;
      console.log("✅ Analytics initialized successfully with ID:", effectiveMeasurementId);
    } catch (error) {
      console.error("❌ Failed to initialize analytics:", error);
    }
  }

  public logEvent(eventName: string, parameters?: Record<string, unknown>): void {
    if (!isAnalyticsEnabled) {
      console.log("🔴 GA DISABLED - Event would be:", eventName, parameters);
      return;
    }

    if (!this.initialized) {
      console.warn("⚠️ Analytics not initialized. Event skipped:", eventName, parameters);
      return;
    }

    try {
      console.log("✅ GA Event:", eventName, parameters);
      this.analyticsService.logEvent(eventName, parameters ?? {});
    } catch (error) {
      console.error("❌ Failed to log analytics event:", error);
    }
  }
}

export default AnalyticsManager;
